import { create } from 'zustand';
import { GridSortModel } from '@mui/x-data-grid-pro';
import { scopedLocalStorage } from '@/lib/browser/scopedStorage';
import type { TheoryActivitySearchCriteria } from '@/api/ApiClient';

const STORAGE_KEY = 'theoryActivityFilters';

/** Default filter criteria for theory activity search */
export const defaultTheoryActivityFilterCriteria: TheoryActivitySearchCriteria = {
  techniqueId: undefined,
  projectId: undefined,
  searchTerm: undefined,
  software: undefined,
  softwareVersion: undefined,
  description: undefined,
  createdByUserId: undefined,
  researchGroup: undefined,
  dateAfter: undefined,
  dateBefore: undefined,
  status: undefined,
  onlyPending: false,
  onlySubmitted: false,
  dataPackageId: undefined,
};

interface TheoryActivityFilterStore {
  /** Current filter criteria for theory activity search */
  filters: TheoryActivitySearchCriteria;
  /** True while the theory activity grid is actively fetching results */
  isSearching: boolean;
  /** Sets search-in-progress state (not persisted) */
  setIsSearching: (isSearching: boolean) => void;
  /** Updates the filter criteria and persists to localStorage */
  setFilters: (filters: TheoryActivitySearchCriteria) => void;
  /** Resets filters to default criteria and removes from localStorage */
  resetFilters: () => void;
  /** Pagination model for the data grid */
  paginationModel: { page: number; pageSize: number };
  /** Updates pagination model */
  setPaginationModel: (
    model:
      | { page: number; pageSize: number }
      | ((prev: { page: number; pageSize: number }) => { page: number; pageSize: number })
  ) => void;
  /** Sort model for the data grid */
  sortModel: GridSortModel;
  /** Updates sort model */
  setSortModel: (model: GridSortModel) => void;
}

export const useTheoryActivityFilterStore = create<TheoryActivityFilterStore>((set, _get) => ({
  filters: (() => {
    if (typeof window === 'undefined') return defaultTheoryActivityFilterCriteria;
    try {
      const stored = scopedLocalStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : defaultTheoryActivityFilterCriteria;
    } catch {
      return defaultTheoryActivityFilterCriteria;
    }
  })(),

  setFilters: (filters) => {
    set({ filters });
    if (typeof window !== 'undefined') {
      scopedLocalStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    }
  },

  resetFilters: () => {
    set({ filters: defaultTheoryActivityFilterCriteria });
    if (typeof window !== 'undefined') {
      scopedLocalStorage.removeItem(STORAGE_KEY);
    }
  },

  paginationModel: { page: 0, pageSize: 25 },
  setPaginationModel: (model) => {
    if (typeof model === 'function') {
      set((state) => ({ paginationModel: model(state.paginationModel) }));
    } else {
      set({ paginationModel: model });
    }
  },

  sortModel: [{ field: 'date', sort: 'desc' }],
  setSortModel: (model) => set({ sortModel: model }),
  isSearching: false,
  setIsSearching: (isSearching) => set({ isSearching }),
}));
